import LoadingLinearProgress from "../../components/SimpleComponents/LoadingLinearProgress";
import styled from "styled-components/macro";
import {
  NAV_HEIGHT_PX,
  Z_INDEX,
  MINIMUM_LOADED_PERCENT_THRESHOLD,
  BREAKPOINT_DESKTOP_XL,
  NAV_HEIGHT_MOBILE_PX,
} from "../../constants/constants";
import {
  useGetPageFromPath,
  useVisibleProcesses,
  useSelectedProcesses,
  useRollupsToDisplay,
  useFilteredProcesses,
  useTrendChartDataDumps,
  useFullRangeDataDump,
  useIsSingleProcessPage,
  useSingleProcessFullRangeDataDump,
  getPgKey,
  useVisibleGroups,
  useTrendsPageFilterState,
  useShifts,
  useFocusRange,
} from "utils/customHooks/genericHooks";
import useQueryString from "utils/customHooks/useQueryString";
import { useTrendBarQueryRangesFromFocusRange } from "components/Dashboard/TrendChart/TrendChartBarsGetter";
import {
  useShouldFetchTrendChartDataDumps,
  useShouldUseDataDumpsForLoadingIndicator,
  useShouldUseDataDumpsForPnlChart,
} from "utils/customHooks/whichServerHooks";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useGetLineRole } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { FlightStandardCandlePerformanceObserver } from "App/FlightPerformanceObserver";
import { useSelector } from "store/useSelector";
import { useMediaQuery } from "@mui/material";
import { useMemo } from "react";

const LoadingIndicatorDashboardPagesStyles = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  z-index: ${Z_INDEX.L10};
  @supports (overflow: overlay) {
    right: -16px;
  }
  top: ${NAV_HEIGHT_MOBILE_PX}px;
  @media (min-width: 600px) {
    top: ${NAV_HEIGHT_PX}px;
  }
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    top: ${NAV_HEIGHT_PX * 1.5}px;
    .MuiPopper-root {
      transform: scale(1.75) translateY(-4px);
      transform-origin: bottom;
    }
  }
`;

const LoadingIndicatorDashboardPages = () => {
  const numProcessesRequiredOnThisPage = useNumProcessesRequiredOnThisPage();
  const rollupsToDisplay = useRollupsToDisplay();
  const fullRangeDataDump = useFullRangeDataDump();
  const { isProcessTilesPage, isAllWorkSchedulesPage, isGroupTilesPage } =
    useGetPageFromPath();
  const isSingleProcessPage = useIsSingleProcessPage();
  const processIdOnSingleProcessPage = useProcessIdOnSingleProcessPage();
  const dataDumpsFullRangeByProcess = useSelector(
    s => s.legacy.dataDumpsFullRangeByProcess
  );

  const singleProcessDataDump = useSingleProcessFullRangeDataDump(
    processIdOnSingleProcessPage
  );
  const shouldUseDataDumps = useShouldUseDataDumpsForLoadingIndicator();

  const { numerator, denominator, progressPercent } = useMemo(() => {
    const numButterflyRollups = Object.keys(rollupsToDisplay).length;

    const numerator = shouldUseDataDumps
      ? isSingleProcessPage && singleProcessDataDump
        ? 1
        : isProcessTilesPage
          ? Object.keys(dataDumpsFullRangeByProcess).length
          : fullRangeDataDump
            ? 1
            : 0
      : numButterflyRollups;
    const denominator = shouldUseDataDumps
      ? isSingleProcessPage && singleProcessDataDump
        ? 1
        : isProcessTilesPage
          ? numProcessesRequiredOnThisPage
          : fullRangeDataDump
            ? 1
            : numProcessesRequiredOnThisPage
      : numProcessesRequiredOnThisPage;

    const progressPercent = numerator / denominator;
    return {
      numerator,
      denominator,
      progressPercent,
    };
  }, [
    shouldUseDataDumps,
    isSingleProcessPage,
    singleProcessDataDump,
    isProcessTilesPage,
    dataDumpsFullRangeByProcess,
    fullRangeDataDump,
    rollupsToDisplay,
    numProcessesRequiredOnThisPage,
  ]);
  if (isAllWorkSchedulesPage && isGroupTilesPage) {
    return null;
  }
  return (
    <LoadingIndicatorDashboardPagesStyles className="doNotPrint">
      <LoadingLinearProgress
        progressPercent={progressPercent}
        numerator={numerator}
        denominator={denominator}
      />
      <FlightStandardCandlePerformanceObserver
        progressPercent={progressPercent}
      />
    </LoadingIndicatorDashboardPagesStyles>
  );
};

export default LoadingIndicatorDashboardPages;

/** based on what page you're on, get the number of processes required before data is ready */
export function useNumProcessesRequiredOnThisPage() {
  const { isTrendsPage, isGroupTilesPage, isHubletPage } = useGetPageFromPath();
  const selectedProcessIdsRef = useSelectedProcesses();
  const visibleProcesses = useVisibleProcesses();

  const filteredProcesses = useFilteredProcesses();
  const parsedQuery = useQueryString();

  const visibleGroups = useVisibleGroups();
  const pgKey = useMemo(
    () => visibleGroups[0] && getPgKey(visibleGroups[0].shortName),
    [visibleGroups]
  );
  const getLineRole = useGetLineRole(pgKey);
  return useMemo(() => {
    const isAtLeastOneProcessWithLineRole =
      visibleGroups.length === 1 &&
      visibleProcesses.some(process => {
        const lineRole = getLineRole(process.id);
        return lineRole && lineRole !== "STANDARD";
      });

    const numProcessesRequired =
      "numprocesses" in parsedQuery // don't load if we're using fake processes
        ? 0
        : visibleProcesses.length === 0
          ? // must have at least one process loaded
            Infinity
          : isTrendsPage
            ? filteredProcesses.length
            : isGroupTilesPage || isHubletPage
              ? // if it's Trends or GroupTiles page we need as many as visibleProcesses
                visibleProcesses.length
              : isAtLeastOneProcessWithLineRole
                ? // for process groups with constraint/leader processes,
                  visibleProcesses.filter(
                    process => getLineRole(process.id) !== "STANDARD"
                  ).length || 0
                : // if it's any other page (Timeline, ProcessTiles) we need the selectedProcesses
                  visibleProcesses.filter(process =>
                    selectedProcessIdsRef.current.includes(process.id)
                  ).length || 0;
    return Math.max(numProcessesRequired, 1); // always need at least 1;
  }, [
    visibleProcesses,
    filteredProcesses,
    parsedQuery,
    isTrendsPage,
    isGroupTilesPage,
    isHubletPage,
    getLineRole,
    visibleGroups,
    selectedProcessIdsRef,
  ]);
}

/** whether or not the data is mostly loaded */
export function useIsStillLoadingDataToStore(isPnlChart = false): boolean {
  const numProcessesRequiredOnThisPage = useNumProcessesRequiredOnThisPage();
  const rollupsToDisplay = useRollupsToDisplay();
  const fullRangeDataDump = useFullRangeDataDump();
  const isSingleProcessPage = useIsSingleProcessPage();
  const processId = useProcessIdOnSingleProcessPage();
  const singleProcessDataDump = useSingleProcessFullRangeDataDump(processId);
  const shouldUseDataDumpsLoadingIndicator =
    useShouldUseDataDumpsForLoadingIndicator();
  const shouldUseDataDumpsPnlChart = useShouldUseDataDumpsForPnlChart();

  const isPrint = useMediaQuery("print");

  return useMemo(() => {
    const dataDumpToUse = isSingleProcessPage
      ? singleProcessDataDump
      : fullRangeDataDump;
    const shouldUseDataDumps = isPnlChart
      ? shouldUseDataDumpsPnlChart
      : shouldUseDataDumpsLoadingIndicator;

    const numRollups = Object.keys(rollupsToDisplay).length;
    const numRollupsRequired = Math.round(numProcessesRequiredOnThisPage);
    if (isPrint) {
      return false;
    }

    // wait until we fetch trend chart rollups for ~90% of processes
    // (don't wait for 100% because sometimes a bad config prevents data fetching for one process)
    return shouldUseDataDumps
      ? !dataDumpToUse
      : numRollups < numRollupsRequired;
  }, [
    isSingleProcessPage,
    singleProcessDataDump,
    fullRangeDataDump,
    rollupsToDisplay,
    numProcessesRequiredOnThisPage,
    shouldUseDataDumpsLoadingIndicator,
    shouldUseDataDumpsPnlChart,
    isPnlChart,
    isPrint,
  ]);
}

/** some customers use data dumps for their trend chart.
 * In that case, we need to count those instead of the standard useIsStillLoadingDataToStore
 */
export function useIsTrendChartStillLoading(): {
  isLoading: boolean;
  percentLoaded: number;
  numerator: number;
  denominator: number;
  explanation: string | null;
} {
  const numProcessesRequiredOnThisPage = useNumProcessesRequiredOnThisPage();
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );
  const { trendChartBarsRanges } = useTrendBarQueryRangesFromFocusRange();
  const isCustomerWhoUsesTrendChartDataDumps =
    useShouldFetchTrendChartDataDumps();
  const trendChartDataDumps = useTrendChartDataDumps();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();
  const shifts = useShifts();
  const focusRange = useFocusRange();

  return useMemo(() => {
    const totalTrendChartRollups = Object.entries(
      allRollupsMetricTrendChartBarsArrays
    ).reduce(
      (acc, [_processId, processTrendBars]) => acc + processTrendBars.length,
      0
    );

    const numTrendChartDataDumps = trendChartDataDumps.length;
    const trendChartDDAProcessesReceived = trendChartDataDumps[0]?.processIds;

    const now = Date.now();
    const currentTrendChartBarRanges = trendChartBarsRanges.filter(
      range => range.ge < now
    );
    const numTrendChartBarsRequired = currentTrendChartBarRanges.length;

    // wait for num askr aggregates for all processes
    const numRollups = totalTrendChartRollups;
    const numRollupsRequired =
      Math.round(numProcessesRequiredOnThisPage) * numTrendChartBarsRequired;

    const shouldUseDDAs =
      isCustomerWhoUsesTrendChartDataDumps && isMidgardDDAEnabled;

    let { numerator, denominator, explanation } = {
      numerator: 0,
      denominator: 1,
      explanation: null as string | null,
    };

    if (shouldUseDDAs) {
      numerator =
        numTrendChartDataDumps * (trendChartDDAProcessesReceived?.length ?? 0);
      denominator = numRollupsRequired;
    } else {
      numerator = numRollups;
      denominator = numRollupsRequired;
    }
    if (numProcessesRequiredOnThisPage > 1) {
      explanation = `${numProcessesRequiredOnThisPage} processes x ${numTrendChartBarsRequired} bars`;
    }

    // in trends page shift mode, count the number of shifts in the focus range
    const shiftsInFocusRange = focusRange
      ? shifts.filter(
          shift =>
            shift.range.ge >= focusRange.ge && shift.range.lt <= focusRange.lt
        )
      : [];
    if (isTrendsPageFilteredByShift) {
      denominator = shiftsInFocusRange.length * numProcessesRequiredOnThisPage;
      explanation = `${numProcessesRequiredOnThisPage} processes x ${shiftsInFocusRange.length} shifts`;
    }

    const percentLoaded = numerator / denominator;

    const isMockData =
      allRollupsMetricTrendChartBarsArrays &&
      Object.values(allRollupsMetricTrendChartBarsArrays)?.[0]?.[0]?.isFake;

    return {
      isLoading:
        !isMockData && // incorrect # of aggregates are supplied in dataConsistency.spec.ts
        (isNaN(percentLoaded) ||
          !isFinite(percentLoaded) ||
          percentLoaded < MINIMUM_LOADED_PERCENT_THRESHOLD),
      percentLoaded,
      numerator: Math.min(numerator, denominator),
      denominator,
      explanation,
    };
  }, [
    numProcessesRequiredOnThisPage,
    allRollupsMetricTrendChartBarsArrays,
    trendChartBarsRanges,
    isCustomerWhoUsesTrendChartDataDumps,
    trendChartDataDumps,
    isMidgardDDAEnabled,
    isTrendsPageFilteredByShift,
    shifts,
    focusRange,
  ]);
}
